import { AccountRole, AccountStatus } from '@/types/generated/common/account'

// Roles assignable via invite / change-role. OWNER is reachable only through
// transfer-ownership, and ADMIN is deprecated — the backend rejects both here,
// so neither is offered. UNSPECIFIED is rejected too.
export const ASSIGNABLE_ROLES = [
  AccountRole.ACCOUNT_ROLE_READ_ONLY,
  AccountRole.ACCOUNT_ROLE_READ_WRITE,
] as const

export function accountRoleTranslationKey(
  role: AccountRole
): 'readOnly' | 'readWrite' | 'owner' | 'admin' | 'unknown' {
  switch (role) {
    case AccountRole.ACCOUNT_ROLE_READ_ONLY:
      return 'readOnly'
    case AccountRole.ACCOUNT_ROLE_READ_WRITE:
      return 'readWrite'
    case AccountRole.ACCOUNT_ROLE_OWNER:
      return 'owner'
    case AccountRole.ACCOUNT_ROLE_ADMIN:
      return 'admin'
    default:
      return 'unknown'
  }
}

// Full-control predicate: OWNER is the top role (manage access, credentials,
// disable, transfer). ADMIN is deprecated and backfilled to OWNER server-side,
// but any legacy ADMIN link we still read carries the same full control.
export function isAccountOwner(role?: AccountRole): boolean {
  return role === AccountRole.ACCOUNT_ROLE_OWNER || role === AccountRole.ACCOUNT_ROLE_ADMIN
}

/**
 * How many accounts count against the plan's `maxAccounts` limit.
 *
 * Mirrors the backend's `owned_count` (public-api `account_handlers.rs`)
 * exactly: owned, and not INACTIVE. INACTIVE is the user's own delete/disable
 * action and releases the slot; every other non-ACTIVE status can still
 * recover on its own, so it keeps its slot.
 *
 * Callers must not pre-filter by status — the settings dialog reads accounts
 * with `showDisabled: true` (it needs the disabled ones for its own toggle),
 * and counting that list raw made every deleted account keep consuming a slot,
 * which pinned "Add account" to the upgrade page forever.
 */
export function countAccountsAgainstPlanCap(
  accounts: readonly { role?: AccountRole; status?: AccountStatus }[]
): number {
  return accounts.filter(
    (account) =>
      isAccountOwner(account.role) && account.status !== AccountStatus.ACCOUNT_STATUS_INACTIVE
  ).length
}
